(() => {
  const PROFILE_KEY = 'onlyfindz-marketplace-profile-v1';
  const CATALOG_SCRIPT_ID = 'MarketplaceCatalogData';
  const bootState = window.__OnlyFindzMarketplaceCatalogBootState || (window.__OnlyFindzMarketplaceCatalogBootState = {
    pending: null,
    timer: null,
    lastRunAt: 0,
    lastPath: ''
  });
  const ELECTRONICS_HINTS = [
    'ssd',
    'router',
    'wifi',
    'wlan',
    'kopfh',
    'headset',
    'smartwatch',
    'watch',
    'laptop',
    'powerbank',
    'kamera',
    'camera',
    'monitor',
    'smart home',
    'bluetooth',
    'fritz',
    'telekom',
    'kingston',
    'samsung',
    'jbl',
    'sennheiser',
    'ring',
    'garmin'
  ];

  function visitorAllowsPersonalization() {
    const consent = window.OnlyFindzConsent && typeof window.OnlyFindzConsent.get === 'function'
      ? window.OnlyFindzConsent.get()
      : null;

    return Boolean(consent && consent.preferences);
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function normalize(value) {
    return String(value ?? '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  function readCatalogBootstrap() {
    const script = document.getElementById(CATALOG_SCRIPT_ID);
    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Could not parse marketplace catalog data', error);
      return null;
    }
  }

  async function loadCatalog() {
    const bootstrap = readCatalogBootstrap();
    if (!bootstrap) return null;

    if (bootstrap.assetUrl) {
      try {
        const response = await fetch(bootstrap.assetUrl, { credentials: 'same-origin' });
        if (!response.ok) return null;
        return await response.json();
      } catch (error) {
        console.error('Could not load marketplace catalog asset', error);
        return null;
      }
    }

    return bootstrap;
  }

  function readProfile() {
    if (!visitorAllowsPersonalization()) {
      return { recentProducts: [], vendorScores: {}, categoryScores: {} };
    }

    try {
      const raw = window.localStorage.getItem(PROFILE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return {
        recentProducts: Array.isArray(parsed.recentProducts)
          ? parsed.recentProducts
          : Array.isArray(parsed.products)
          ? parsed.products
          : [],
        vendorScores: parsed.vendorScores && typeof parsed.vendorScores === 'object' ? parsed.vendorScores : parsed.vendors || {},
        categoryScores:
          parsed.categoryScores && typeof parsed.categoryScores === 'object' ? parsed.categoryScores : parsed.categories || {},
      };
    } catch (error) {
      return { recentProducts: [], vendorScores: {}, categoryScores: {} };
    }
  }

  function writeProfile(profile) {
    if (!visitorAllowsPersonalization()) return;

    try {
      window.localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
    } catch (error) {
      console.error('Could not persist marketplace profile', error);
    }
  }

  function clearProfile() {
    try {
      window.localStorage.removeItem(PROFILE_KEY);
    } catch (error) {
      console.error('Could not clear marketplace profile', error);
    }
  }

  function money(value, currencyCode = 'EUR') {
    const amount = Number(value || 0);
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currencyCode || 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  function shortText(value, maxLength) {
    const source = String(value || '').trim();
    if (!source || source.length <= maxLength) return source;
    return `${source.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`;
  }

  function cleanDescription(value) {
    let s = String(value || '').trim();
    // Strip data-scraper artefacts that leaked into product descriptions
    s = s.replace(/^(Keine Designvorlage\s*|Produktbeschreibung\s*)+/i, '');
    // Strip injected .fixlogo CSS blocks
    s = s.replace(/\s*\.fixlogo\s*\{[^}]*\}/gi, '');
    return s.trim();
  }

  function compactCategoryLabel(value) {
    const source = String(value || '').trim();
    if (!source) return '';

    const slashParts = source
      .split('/')
      .map((part) => part.trim())
      .filter(Boolean);
    const candidate = slashParts.length > 1 ? slashParts[slashParts.length - 1] : source;

    return shortText(candidate.replace(/\s*\(.*?\)\s*/g, ' ').replace(/\s{2,}/g, ' ').trim(), 24);
  }

  function shortTitle(value, maxLength = 56) {
    return shortText(String(value || '').replace(/\s*[–-]\s*/g, ' – ').trim(), maxLength);
  }

  function productDetailUrl(handle) {
    return `/products/${handle}`;
  }

  // Returns the highest-resolution image available for a product.
  // Prefers images >= 400px wide; falls back to featured image if nothing qualifies.
  function bestImage(product) {
    const imgs = product.images;
    if (!imgs?.length) return product.image || null;
    const MIN_WIDTH = 400;
    const qualified = imgs.filter(img => (img.width || 0) >= MIN_WIDTH);
    const pool = qualified.length ? qualified : imgs;
    return pool.reduce((best, img) => {
      const area = (img.width || 0) * (img.height || 0);
      const bestArea = (best.width || 0) * (best.height || 0);
      return area > bestArea ? img : best;
    }, pool[0]);
  }

  function imageUrlWithWidth(url, width) {
    if (!url || !width) return url || '';
    const cleanUrl = String(url);
    const withoutWidth = cleanUrl
      .replace(/([?&])width=\d+/g, '$1')
      .replace('?&', '?')
      .replace(/&&+/g, '&')
      .replace(/[?&]$/, '');
    const separator = withoutWidth.includes('?') ? '&' : '?';
    return `${withoutWidth}${separator}width=${width}`;
  }

  function responsiveImageMarkup(image, alt, options = {}) {
    if (!image?.url) return '';

    const naturalWidth = Number(image.width || 0);
    const naturalHeight = Number(image.height || 0);
    const requestedWidths = Array.isArray(options.widths) && options.widths.length ? options.widths : [240, 360, 540, 720];
    const usableWidths = requestedWidths
      .filter((width) => !naturalWidth || width <= naturalWidth)
      .filter((width, index, array) => array.indexOf(width) === index);
    const finalWidths = usableWidths.length ? usableWidths : requestedWidths.slice(0, 1);
    const srcWidth = finalWidths[finalWidths.length - 1];
    const srcset = finalWidths.map((width) => `${imageUrlWithWidth(image.url, width)} ${width}w`).join(', ');
    const widthAttr = naturalWidth ? ` width="${naturalWidth}"` : '';
    const heightAttr = naturalHeight ? ` height="${naturalHeight}"` : '';
    const sizesAttr = options.sizes ? ` sizes="${escapeHtml(options.sizes)}"` : '';
    const fetchpriorityAttr = options.fetchpriority ? ` fetchpriority="${escapeHtml(options.fetchpriority)}"` : '';
    const decodingAttr = options.decoding ? ` decoding="${escapeHtml(options.decoding)}"` : ' decoding="async"';

    return `<img class="${escapeHtml(options.className || '')}" src="${escapeHtml(
      imageUrlWithWidth(image.url, srcWidth)
    )}" srcset="${escapeHtml(srcset)}"${sizesAttr} alt="${escapeHtml(alt || image.alt || '')}" loading="${escapeHtml(
      options.loading || 'lazy'
    )}"${fetchpriorityAttr}${decodingAttr}${widthAttr}${heightAttr}>`;
  }

  function scoreProduct(product, profile) {
    const haystack = normalize(
      [
        product.title,
        product.vendor,
        product.productType,
        product.primaryCollection?.title,
        product.description
      ].join(' ')
    );

    let score = 0;
    ELECTRONICS_HINTS.forEach((hint) => {
      if (haystack.includes(hint)) score += 6;
    });

    if (bestImage(product)?.url) score += 10;
    if (product.primaryCollection?.handle) score += 4;
    if (Number(product.salePercentage || 0) > 0) score += 6 + Number(product.salePercentage || 0) / 10;
    if (Number(product.price?.amount || 0) > 0) score += 2;

    if (profile?.vendorScores) score += Number(profile.vendorScores[product.vendor] || 0) * 6;
    if (profile?.categoryScores) score += Number(profile.categoryScores[product.primaryCollection?.title] || 0) * 6;
    if (profile?.recentProducts?.includes(product.handle)) score += 20 - profile.recentProducts.indexOf(product.handle);

    return score;
  }

  function scoreCollection(collection) {
    const haystack = normalize([collection.title, collection.description].join(' '));
    let score = Number(collection.productsCount || 0);
    ELECTRONICS_HINTS.forEach((hint) => {
      if (haystack.includes(hint)) score += 25;
    });
    if (collection.image?.url) score += 8;
    return score;
  }

  function sortProducts(products, profile) {
    return [...products].sort((left, right) => {
      const scoreDelta = scoreProduct(right, profile) - scoreProduct(left, profile);
      if (scoreDelta !== 0) return scoreDelta;
      return Number(right.price?.amount || 0) - Number(left.price?.amount || 0);
    });
  }

  function buildProductCard(product, options = {}) {
    const productUrl = options.productUrl || productDetailUrl(product.handle);
    const categoryLabel =
      compactCategoryLabel(product.primaryCollection?.title) ||
      compactCategoryLabel(product.productType) ||
      compactCategoryLabel(product.vendor) ||
      'Sortiment';
    const showExcerpt = options.showExcerpt === true;
    const _cardImg = bestImage(product);
    const imageMarkup = _cardImg?.url
      ? responsiveImageMarkup(_cardImg, product.title, {
          className: 'marketplace-product-card__image',
          widths: [160, 240, 320],
          sizes: '(min-width: 1200px) 18rem, (min-width: 750px) 24vw, 72vw',
          loading: 'lazy'
        })
      : `<div class="marketplace-product-card__placeholder" aria-hidden="true"></div>`;

    return `
      <article
        class="marketplace-product-card marketplace-product-card--hydrated"
        id="product-${escapeHtml(product.handle)}"
        data-marketplace-product-card
        data-product-handle="${escapeHtml(product.handle)}"
        data-product-title="${escapeHtml(product.title)}"
        data-product-vendor="${escapeHtml(product.vendor)}"
        data-product-category="${escapeHtml(categoryLabel)}"
      >
        <a href="${escapeHtml(productUrl)}" class="marketplace-product-card__media-link full-unstyled-link">
          <div class="marketplace-product-card__media">
            ${
              Number(product.salePercentage || 0) > 0
                ? `<span class="marketplace-product-card__badge">-${Math.round(product.salePercentage)}%</span>`
                : ''
            }
            ${imageMarkup}
          </div>
        </a>
        <wishlist-button
          class="wishlist-button"
          data-product-handle="${escapeHtml(product.handle)}"
          data-product-title="${escapeHtml(product.title)}"
          data-product-price="${money(product.price?.amount, product.price?.currencyCode)}"
          data-product-image="${escapeHtml(_cardImg?.url || '')}"
          data-product-url="${escapeHtml(productUrl)}"
        >
          <button type="button" class="wishlist-button__trigger" aria-pressed="false" aria-label="Zur Merkliste hinzufügen">
            <span class="visually-hidden wishlist-button__label">Zur Merkliste hinzufügen</span>
            <span class="wishlist-button__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                <path d="M12 21.35 10.55 20C5.4 15.24 2 12.09 2 8.25 2 5.1 4.42 2.75 7.5 2.75c1.74 0 3.41.82 4.5 2.1 1.09-1.28 2.76-2.1 4.5-2.1 3.08 0 5.5 2.35 5.5 5.5 0 3.84-3.4 6.99-8.55 11.76L12 21.35Z"></path>
              </svg>
            </span>
          </button>
        </wishlist-button>
        <div class="marketplace-product-card__body">
          <p class="marketplace-product-card__meta">
            ${product.vendor ? escapeHtml(product.vendor) : ''}
            ${categoryLabel ? `<span>${escapeHtml(categoryLabel)}</span>` : ''}
          </p>
          <h3 class="marketplace-product-card__title">
            <a href="${escapeHtml(productUrl)}" class="full-unstyled-link">${escapeHtml(options.titleLength ? shortTitle(product.title, options.titleLength) : product.title)}</a>
          </h3>
          ${
            showExcerpt && product.excerpt
              ? `<p class="marketplace-product-card__excerpt">${escapeHtml(shortText(cleanDescription(product.excerpt), options.excerptLength || 132))}</p>`
              : ''
          }
          <div class="marketplace-product-card__pricing">
            <span class="marketplace-product-card__price">${money(product.price?.amount, product.price?.currencyCode)}</span>
            ${
              Number(product.compareAtPrice?.amount || 0) > Number(product.price?.amount || 0)
                ? `<span class="marketplace-product-card__compare">${money(
                    product.compareAtPrice?.amount,
                    product.compareAtPrice?.currencyCode || product.price?.currencyCode
                  )}</span>`
                : ''
            }
          </div>
        </div>
      </article>
    `;
  }

  function buildCollectionCard(collection) {
    const imageMarkup = collection.image?.url
      ? `<img class="mkt-col-card__img" src="${escapeHtml(collection.image.url)}" alt="${escapeHtml(
          collection.image.alt || collection.title
        )}" loading="lazy">`
      : `<div class="mkt-col-card__placeholder" aria-hidden="true">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
          </svg>
        </div>`;

    return `
      <li>
        <a href="${escapeHtml(collection.url)}" class="mkt-col-card">
          <div class="mkt-col-card__media">${imageMarkup}</div>
          <div class="mkt-col-card__body">
            <h3 class="mkt-col-card__title">${escapeHtml(collection.title)}</h3>
            <p class="mkt-col-card__count">${escapeHtml(String(collection.productsCount || 0))} Produkte</p>
            <span class="mkt-col-card__arrow" aria-hidden="true">
              Entdecken
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
            </span>
          </div>
        </a>
      </li>
    `;
  }

  function buildDetailGallery(product) {
    const images = Array.isArray(product.images) && product.images.length ? product.images : product.image ? [product.image] : [];
    if (!images.length) {
      return `<div class="marketplace-product-detail__gallery-empty" aria-hidden="true"></div>`;
    }

    const primary = images[0];
    const thumbs = images.slice(1, 5);

    return `
      <div class="marketplace-product-detail__gallery">
        <div class="marketplace-product-detail__hero-image">
          <img src="${escapeHtml(primary.url)}" alt="${escapeHtml(primary.alt || product.title)}" loading="eager">
        </div>
        ${
          thumbs.length
            ? `<div class="marketplace-product-detail__thumbs">
                ${thumbs
                  .map(
                    (image) => `
                      <div class="marketplace-product-detail__thumb">
                        <img src="${escapeHtml(image.url)}" alt="${escapeHtml(image.alt || product.title)}" loading="lazy">
                      </div>
                    `
                  )
                  .join('')}
              </div>`
            : ''
        }
      </div>
    `;
  }

  function trackCatalogProductView(product) {
    const profile = readProfile();
    profile.recentProducts = [product.handle, ...(profile.recentProducts || []).filter((item) => item !== product.handle)].slice(0, 24);
    if (product.vendor) {
      profile.vendorScores[product.vendor] = (profile.vendorScores[product.vendor] || 0) + 2;
    }
    const categoryLabel = product.primaryCollection?.title || product.productType || product.vendor || '';
    if (categoryLabel) {
      profile.categoryScores[categoryLabel] = (profile.categoryScores[categoryLabel] || 0) + 3;
    }
    writeProfile(profile);
    return profile;
  }

  function buildRecommendations(products, limit = 4) {
    return products
      .slice(0, limit)
      .map((product) => buildProductCard(product, { titleLength: 54, excerptLength: 104 }))
      .join('');
  }

  function getSimilarProducts(product, catalog, profile) {
    return [...(catalog.products || [])]
      .filter((candidate) => candidate.handle !== product.handle)
      .map((candidate) => {
        let similarity = 0;
        if (product.vendor && candidate.vendor === product.vendor) similarity += 18;
        if (product.primaryCollection?.handle && candidate.primaryCollection?.handle === product.primaryCollection.handle) similarity += 22;
        if (product.productType && candidate.productType === product.productType) similarity += 12;
        return { ...candidate, _similarity: similarity + scoreProduct(candidate, profile) };
      })
      .sort((left, right) => right._similarity - left._similarity);
  }

  function renderProductDetail(catalog) {
    const params = new URLSearchParams(window.location.search);
    const handle = params.get('product');
    // Redirect legacy ?product= URLs to the real product page with buy buttons
    if (handle) {
      window.location.replace(`/products/${handle}`);
      return true;
    }
    return false;
  }

  function renderHomepage(catalog) {
    const home = document.querySelector('.marketplace-home[data-marketplace-section]');
    if (!home || !catalog.products?.length) return;

    const profile = readProfile();
    const rankedProducts = sortProducts(catalog.products, profile);
    const selectedProducts = rankedProducts.slice(0, 12);
    // Sort and slice raw collections
    const rankedCollections = [...(catalog.collections || [])]
      .sort((left, right) => scoreCollection(right) - scoreCollection(left))
      .slice(0, 18); // oversample before dedup

    // Deduplicate by normalised title key (plural/singular, spacing, hyphens)
    // Mirrors the Liquid dedup in marketplace-home.liquid so JS and SSR stay in sync.
    const _colSeenKeys = new Set();
    const _colSeenUrls = new Set();
    const selectedCollections = [];
    for (const col of rankedCollections) {
      const rawKey = (col.title || '')
        .toLowerCase()
        .replace(/[\s\-_]+/g, '')
        .replace(/s$/, ''); // strip trailing s → "smartwatch" === "smartwatches"
      const urlKey = (col.url || '').split('?')[0].toLowerCase();
      if (!_colSeenKeys.has(rawKey) && !_colSeenUrls.has(urlKey)) {
        _colSeenKeys.add(rawKey);
        _colSeenUrls.add(urlKey);
        selectedCollections.push(col);
        if (selectedCollections.length >= 9) break;
      }
    }

    // ── Category sidebar lists ────────────────────────────────────────────
    // [data-js-ready] triggers the CSS fade-in (opacity 0→1) defined in
    // section-marketplace-home.css. The initial opacity:0 lives in
    // marketplace-global.css (head-loaded) so the SSR version is never visible.
    home.querySelectorAll('.marketplace-home__categories-list').forEach((list) => {
      list.innerHTML = selectedCollections
        .map(
          (collection) => `
            <li>
              <a href="${escapeHtml(collection.url)}" class="marketplace-home__category-link link link--text">
                <span>${escapeHtml(compactCategoryLabel(collection.title) || shortText(collection.title, 32))}</span>
                <span aria-hidden="true">›</span>
              </a>
            </li>
          `
        )
        .join('');
      list.setAttribute('data-js-ready', '');
    });

    // Pick hero products with vendor diversity so lower homepage recommendations
    // can avoid duplicating the server-rendered hero/deal merchandising.
    const slides = [...home.querySelectorAll('[data-marketplace-slide]')];
    const _heroNeeded = Math.max(slides.length, 2);
    const heroProducts = [];
    const heroVendors = new Set();
    for (const p of selectedProducts) {
      if (!bestImage(p)?.url) continue; // skip products without a usable image
      if (heroVendors.has(p.vendor)) continue; // skip same vendor
      heroProducts.push(p);
      heroVendors.add(p.vendor);
      if (heroProducts.length >= _heroNeeded) break;
    }
    // Fallback: fill remaining slots ignoring vendor constraint
    if (heroProducts.length < _heroNeeded) {
      for (const p of selectedProducts) {
        if (!bestImage(p)?.url) continue;
        if (heroProducts.some(hp => hp.handle === p.handle)) continue;
        heroProducts.push(p);
        if (heroProducts.length >= _heroNeeded) break;
      }
    }
    const heroHandles = new Set(heroProducts.map(p => p.handle));

    // ── Deal card ─────────────────────────────────────────────────────────
    // Exclude hero products AND hero vendors from lower recommendation rails.
    const heroVendorSet = new Set(heroProducts.map(p => p.vendor));
    const dealPool = selectedProducts.filter(p => !heroHandles.has(p.handle));
    const dealProduct =
      dealPool.find(p => Number(p.salePercentage || 0) > 0 && !heroVendorSet.has(p.vendor)) ||
      dealPool.find(p => Number(p.salePercentage || 0) > 0) ||
      dealPool.find(p => !heroVendorSet.has(p.vendor)) ||
      dealPool[0] ||
      selectedProducts[0];

    // ── Products heading ──────────────────────────────────────────────────
    const productsHeading = home.querySelector('[data-marketplace-products-heading]');
    if (productsHeading && profile?.recentProducts?.length) {
      productsHeading.textContent = 'Für dich ausgewählt';
    }

    // ── Product grid ──────────────────────────────────────────────────────
    const productGrid = home.querySelector('[data-marketplace-personalization-grid]');
    if (productGrid) {
      const visibleCount = Number(productGrid.dataset.visibleCount || 6);
      // Exclude products already shown in hero or deal card
      const usedHandles = new Set([...heroHandles, dealProduct?.handle].filter(Boolean));
      const gridProducts = [...selectedProducts.filter(p => !usedHandles.has(p.handle)), ...selectedProducts];
      productGrid.innerHTML = gridProducts
        .filter((p, i, arr) => arr.findIndex(x => x.handle === p.handle) === i) // dedup
        .slice(0, visibleCount)
        .map((product) => buildProductCard(product, { showExcerpt: false }))
        .join('');
      productGrid.setAttribute('data-js-ready', '');
    }
  }

  function renderCollectionsIndex(catalog) {
    if (window.location.pathname.replace(/\/+$/, '') !== '/collections') return;
    const list = document.querySelector('[data-marketplace-collections-list]');
    if (!list || !catalog.collections?.length) return;
    list.innerHTML = catalog.collections.slice(0, 30).map(buildCollectionCard).join('');
  }

  function renderCollectionFallback(catalog) {
    const emptyState = document.querySelector('[data-marketplace-collection-fallback]');
    if (!emptyState) return;

    const handle = emptyState.getAttribute('data-collection-handle');
    const productsPerPage = Number(emptyState.getAttribute('data-products-per-page') || 16);
    const profile = readProfile();

    let products = [];
    if (handle === 'all') {
      products = sortProducts(catalog.products || [], profile);
    } else {
      const collection = (catalog.collections || []).find((item) => item.handle === handle);
      const byHandle = new Map((catalog.products || []).map((product) => [product.handle, product]));
      products = sortProducts(
        (collection?.productHandles || []).map((productHandle) => byHandle.get(productHandle)).filter(Boolean),
        profile
      );
    }

    if (!products.length) return;

    emptyState.innerHTML = `
      <div class="marketplace-catalog-fallback">
        <div class="title-wrapper center">
          <h2 class="title title--primary">Unsere Produkte</h2>
        </div>
        <div class="marketplace-catalog-fallback__grid" role="list">
          ${products.slice(0, productsPerPage).map((product) => buildProductCard(product)).join('')}
        </div>
      </div>
    `;
  }

  async function bootCatalogRendering() {
    if (!visitorAllowsPersonalization()) {
      clearProfile();
    }

    const catalog = await loadCatalog();
    if (!catalog) return;

    if (renderProductDetail(catalog)) return;

    renderHomepage(catalog);
    renderCollectionsIndex(catalog);
    renderCollectionFallback(catalog);
  }

  function scheduleCatalogBoot() {
    const pathKey = `${window.location.pathname}${window.location.search}`;
    const now = Date.now();

    if (bootState.pending) return bootState.pending;
    if (bootState.timer) window.clearTimeout(bootState.timer);

    if (bootState.lastPath === pathKey && now - bootState.lastRunAt < 250) {
      return Promise.resolve();
    }

    bootState.pending = new Promise((resolve) => {
      bootState.timer = window.setTimeout(() => {
        bootState.timer = null;
        bootState.lastRunAt = Date.now();
        bootState.lastPath = pathKey;

        Promise.resolve(bootCatalogRendering())
          .catch((error) => {
            console.error('Marketplace catalog boot failed', error);
          })
          .finally(() => {
            bootState.pending = null;
            resolve();
          });
      }, 80);
    });

    return bootState.pending;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleCatalogBoot, { once: true });
  } else {
    scheduleCatalogBoot();
  }

  document.addEventListener('shopify:section:load', scheduleCatalogBoot);
  window.addEventListener('onlyfindz:consent-updated', scheduleCatalogBoot);
})();



